import React, { useMemo, useEffect } from "react";

import { useOverviewContext } from "@features/overview-v2/hooks";
import { type OverviewSavedView } from "@features/overview-v2/types";
import { useFuzzySearch } from "@hooks/use-fuzzy-search";
import { IconButton } from "@shared/buttons";
import { TextInput } from "@shared/text-input";

import { SavedViewsMenuRow } from "./saved-views-menu-row";

export interface SavedViewsListProps {
  readonly isMenuOpen: boolean;
  readonly onSelected: (view: OverviewSavedView) => void;
  readonly onEnableReordering: () => void;
}

export const SavedViewsList = ({
  isMenuOpen,
  onSelected,
  onEnableReordering,
}: SavedViewsListProps) => {
  const { savedViews, selectedSavedView } = useOverviewContext();

  const originalIndexes = useMemo(() => {
    return savedViews.reduce(
      (prev: { [key in OverviewSavedView["id"]]: number }, curr, i) => ({
        ...prev,
        [curr.id]: i,
      }),
      {},
    );
  }, [savedViews]);

  // TODO: Move search related functionality further down in state into the SavedViewsList.
  const [filteredOptions, { search, setSearch, clearSearch }] = useFuzzySearch(
    savedViews,
    {
      searchKeys: ["name"],
    },
  );

  useEffect(() => {
    if (isMenuOpen) {
      clearSearch();
    }
  }, [isMenuOpen, clearSearch]);

  return (
    <>
      <div className="px-4 mt-1 mb-2">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-bold m-0">Saved views</h4>
          <IconButton
            className="text-cosmic text-xs font-bold px-1"
            leftIcon={<i className="fa-regular fa-sharp fa-bars" />}
            onClick={onEnableReordering}
          >
            REORDER
          </IconButton>
        </div>
        <TextInput
          className="w-full"
          rightIcon={
            <i className="fa-regular fa-sharp fa-magnifying-glass text-primary" />
          }
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        {filteredOptions.map((savedView) => (
          <SavedViewsMenuRow
            key={savedView.id}
            isDefault={originalIndexes[savedView.id] === 0}
            savedView={savedView}
            isSelected={savedView.id === selectedSavedView?.id}
            onClick={() => {
              onSelected(savedView);
            }}
          />
        ))}
      </div>
    </>
  );
};
